import { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Typography,
  Box,
  CircularProgress,
  TablePagination,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { cancelInvite, resendInvite, fetchInvites } from '../../redux/slices/inviteSlice';
import InviteDetailsModal from './InviteDetailsModal';
import ResendInviteDialog from './ResendInviteDialog';
import CancelInviteDialog from './CancelInviteDialog';
import PermissionGate from '../common/PermissionGate';

/**
 * Invite Table
 * 
 * Displays invites with actions (cancel, resend, copy link)
 * Phase 4 M3: Invite Management
 */
const InviteTable = ({ invites, total, page, limit, loading }) => {
  const dispatch = useDispatch();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInvite, setSelectedInvite] = useState(null);
  
  // Modal & Dialog states
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Handle menu open
  const handleMenuOpen = (event, invite) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvite(invite);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle view details
  const handleViewDetails = () => {
    setDetailsModalOpen(true);
    handleMenuClose();
  };

  // Handle cancel invite - open dialog
  const handleCancelClick = () => {
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  // Handle cancel invite - confirm
  const handleCancelConfirm = async (inviteId) => {
    try {
      await dispatch(cancelInvite(inviteId)).unwrap();
      
      // Refresh list
      dispatch(fetchInvites({ page, limit }));
    } catch (error) {
      throw error;
    }
  };

  // Handle resend invite - open dialog
  const handleResendClick = () => {
    setResendDialogOpen(true);
    handleMenuClose();
  };

  // Handle resend invite - confirm
  const handleResendConfirm = async (inviteId) => {
    try {
      await dispatch(resendInvite(inviteId)).unwrap();
      
      // Refresh list
      dispatch(fetchInvites({ page, limit }));
    } catch (error) {
      throw error;
    }
  };

  // Handle copy invite link
  const handleCopyLink = () => {
    if (!selectedInvite) return;

    const baseUrl = window.location.origin;
    const inviteUrl = selectedInvite.isExistingUser
      ? `${baseUrl}/invite/accept/${selectedInvite.token}`
      : `${baseUrl}/invite/register/${selectedInvite.token}`;

    navigator.clipboard.writeText(inviteUrl).then(() => {
      // Could show a toast notification here
      console.log('Link copied to clipboard');
    });

    handleMenuClose();
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    dispatch(fetchInvites({ page: newPage + 1, limit }));
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    dispatch(fetchInvites({ page: 1, limit: parseInt(event.target.value, 10) }));
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'default';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'accepted':
        return 'Kabul Edildi';
      case 'rejected':
        return 'Reddedildi';
      case 'expired':
        return 'Süresi Doldu';
      case 'cancelled':
        return 'İptal Edildi';
      default:
        return status;
    }
  };

  // Loading state
  if (loading && invites.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Davetler yükleniyor...
        </Typography>
      </Paper>
    );
  }

  // Empty state
  if (!loading && invites.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Henüz davet göndermediniz
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Yeni kullanıcı davet etmek için "Yeni Davet" butonuna tıklayın
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Kullanıcı Tipi</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Gönderen</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.map((invite) => (
              <TableRow key={invite._id} hover>
                {/* Email */}
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {invite.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {invite.isExistingUser ? 'Mevcut Kullanıcı' : 'Yeni Kullanıcı'}
                    </Typography>
                  </Box>
                </TableCell>

                {/* User Type */}
                <TableCell>
                  <Chip
                    label={invite.userType === 'company_member' ? 'Şirket Üyesi' : invite.userType}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={getStatusLabel(invite.status)}
                    color={getStatusColor(invite.status)}
                    size="small"
                  />
                </TableCell>

                {/* Invited By */}
                <TableCell>
                  <Typography variant="body2">
                    {invite.invitedBy?.firstName} {invite.invitedBy?.lastName}
                  </Typography>
                </TableCell>

                {/* Date */}
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(invite.createdAt), 'dd MMM yyyy', { locale: tr })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(invite.createdAt), 'HH:mm')}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, invite)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        onPageChange={handlePageChange}
        rowsPerPage={limit}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Sayfa başına:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* View Details */}
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Detayları Göster</ListItemText>
        </MenuItem>

        {selectedInvite?.status === 'pending' && <Divider />}

        {/* Copy Link (only for pending invites) */}
        {selectedInvite?.status === 'pending' && (
          <MenuItem onClick={handleCopyLink}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Linki Kopyala</ListItemText>
          </MenuItem>
        )}

        {/* Resend (only for pending invites) */}
        {selectedInvite?.status === 'pending' && (
          <PermissionGate permission="invites.resend">
            <MenuItem onClick={handleResendClick}>
              <ListItemIcon>
                <SendIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Tekrar Gönder</ListItemText>
            </MenuItem>
          </PermissionGate>
        )}

        {/* Cancel (only for pending invites) */}
        {selectedInvite?.status === 'pending' && (
          <PermissionGate permission="invites.cancel">
            <>
              <Divider />
              <MenuItem onClick={handleCancelClick}>
                <ListItemIcon>
                  <CancelIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <Typography color="error">İptal Et</Typography>
                </ListItemText>
              </MenuItem>
            </>
          </PermissionGate>
        )}
      </Menu>

      {/* Modals & Dialogs */}
      <InviteDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        invite={selectedInvite}
      />

      <ResendInviteDialog
        open={resendDialogOpen}
        onClose={() => setResendDialogOpen(false)}
        invite={selectedInvite}
        onConfirm={handleResendConfirm}
      />

      <CancelInviteDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        invite={selectedInvite}
        onConfirm={handleCancelConfirm}
      />
    </Paper>
  );
};

InviteTable.propTypes = {
  invites: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  loading: PropTypes.bool
};

InviteTable.defaultProps = {
  loading: false
};

export default InviteTable;

